// Copyright (c) 2025-2026 Motswagae Holding (Pty) Ltd. All Rights Reserved.
// CIPC Registration: 2019/443098/07
// Author: Velly Sipho Phaloane
//
// This source code is the proprietary and confidential property of
// Motswagae Holding (Pty) Ltd, trading as 360Wallet.
// Unauthorized copying, modification, distribution, or use of this
// software, in whole or in part, is strictly prohibited.
//
// Protected under the Copyright Act 98 of 1978 (South Africa)
// and international copyright treaties.
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { getSettlements, generateSettlement, exportSettlements, exportSettlementsPDF, generateMonthlyBilling, updateSettlementStatus, getBanks } from "../lib/api";
import { toast } from "sonner";
import { FileText, CalendarIcon, Download, RefreshCw, ArrowRight, CheckCircle2, FileDown } from "lucide-react";
import { format } from "date-fns";

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 2
  }).format(amount);
};

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-ZA', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const StatusBadge = ({ status }) => {
  const statusStyles = {
    pending: "status-warning",
    processing: "status-info",
    completed: "status-success",
    failed: "status-error"
  };

  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${statusStyles[status] || 'status-neutral'}`}>
      {status}
    </span>
  );
};

const Settlements = () => {
  const [settlements, setSettlements] = useState([]);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [filterDate, setFilterDate] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  const fetchData = async () => {
    try {
      const params = {};
      if (filterDate) params.settlement_date = filterDate;
      if (filterStatus !== "all") params.status = filterStatus;
      
      const [settlementsRes, banksRes] = await Promise.all([
        getSettlements(params),
        getBanks()
      ]);
      setSettlements(settlementsRes.data);
      setBanks(banksRes.data);
    } catch (error) {
      toast.error("Failed to load settlements");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filterDate, filterStatus]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      const res = await generateSettlement(dateStr);
      if (res.data.settlements.length === 0) {
        toast.info("No transactions found for this date");
      } else {
        toast.success(`Generated ${res.data.settlements.length} settlement records`);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate settlement");
    } finally {
      setGenerating(false);
    }
  };

  const handleExport = async (date) => {
    try {
      const res = await exportSettlements(date);
      // Create and download JSON file
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `settlement_${date}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Settlement file exported");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to export settlement");
    }
  };

  const handleExportPDF = async (date) => {
    try {
      const res = await exportSettlementsPDF(date);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `settlement_report_${date}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF report downloaded");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate PDF");
    }
  };

  const handleMonthlyBilling = async (bankCode) => {
    const month = format(selectedDate, "yyyy-MM");
    try {
      const res = await generateMonthlyBilling(bankCode, month);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `billing_${bankCode}_${month}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Monthly billing PDF downloaded");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate billing report");
    }
  };

  const handleStatusUpdate = async (settlementId, newStatus) => {
    try {
      await updateSettlementStatus(settlementId, newStatus);
      toast.success("Settlement status updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  // Calculate summary
  const totalAmount = settlements.reduce((sum, s) => sum + s.total_amount, 0);
  const totalFees = settlements.reduce((sum, s) => sum + s.total_fees, 0);
  const totalTx = settlements.reduce((sum, s) => sum + s.transaction_count, 0);

  // Get unique settlement dates for filter
  const uniqueDates = [...new Set(settlements.map(s => s.settlement_date))].sort().reverse();

  return (
    <div className="space-y-6" data-testid="settlements-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl">Settlements</h1>
          <p className="text-muted-foreground">Daily reconciliation and billing reports</p>
        </div>
      </div>

      {/* Generate Settlement */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Generate Settlement</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div className="space-y-2">
              <Label>Settlement Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[200px] justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {selectedDate ? format(selectedDate, "PP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <Button 
              onClick={handleGenerate} 
              disabled={generating}
              className="shadow-[0_0_15px_rgba(59,130,246,0.3)]"
              data-testid="generate-settlement-btn"
            >
              {generating ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <FileText className="h-4 w-4 mr-2" />
                  Generate Settlement
                </>
              )}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Aggregates all successful transactions for the selected date by issuer-acquirer pairs
          </p>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Records</p>
            <p className="text-2xl font-bold font-mono mt-1">{settlements.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Amount</p>
            <p className="text-2xl font-bold font-mono mt-1">{formatCurrency(totalAmount)}</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Fees</p>
            <p className="text-2xl font-bold font-mono mt-1 text-accent">{formatCurrency(totalFees)}</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Transactions</p>
            <p className="text-2xl font-bold font-mono mt-1">{totalTx}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <Select value={filterDate || "all-dates"} onValueChange={(v) => setFilterDate(v === "all-dates" ? "" : v)}>
              <SelectTrigger className="w-full sm:w-48" data-testid="filter-date-select">
                <SelectValue placeholder="All Dates" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all-dates">All Dates</SelectItem>
                {uniqueDates.map(date => (
                  <SelectItem key={date} value={date}>{formatDate(date)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full sm:w-48" data-testid="filter-status-select">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="processing">Processing</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
            {filterDate && (
              <>
                <Button 
                  variant="outline" 
                  onClick={() => handleExport(filterDate)}
                  data-testid="export-json-btn"
                >
                  <Download className="h-4 w-4 mr-2" />
                  JSON
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => handleExportPDF(filterDate)}
                  data-testid="export-pdf-btn"
                >
                  <FileDown className="h-4 w-4 mr-2" />
                  PDF Report
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Settlements Table */}
      <Card className="bg-card border-border">
        <CardContent className="p-0">
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Flow</TableHead>
                <TableHead>Transactions</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Fees</TableHead>
                <TableHead>Net Position</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <div className="animate-pulse text-muted-foreground">Loading settlements...</div>
                  </TableCell>
                </TableRow>
              ) : settlements.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <div className="flex flex-col items-center gap-2">
                      <FileText className="h-8 w-8 text-muted-foreground" />
                      <p className="text-muted-foreground">No settlement records found</p>
                      <p className="text-sm text-muted-foreground">Generate a settlement for a date with transactions</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                settlements.map((settlement) => (
                  <TableRow key={settlement.id}>
                    <TableCell>
                      <span className="font-mono">{formatDate(settlement.settlement_date)}</span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm">
                        <span className="text-primary">{settlement.issuer_bank_code}</span>
                        <ArrowRight className="h-3 w-3 text-muted-foreground" />
                        <span className="text-accent">{settlement.acquirer_bank_code}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="font-mono">{settlement.transaction_count}</span>
                    </TableCell>
                    <TableCell>
                      <span className="font-mono">{formatCurrency(settlement.total_amount)}</span>
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-accent">{formatCurrency(settlement.total_fees)}</span>
                    </TableCell>
                    <TableCell>
                      <span className="font-mono font-medium">{formatCurrency(settlement.net_position)}</span>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={settlement.status} />
                    </TableCell>
                    <TableCell className="text-right">
                      {settlement.status === "pending" && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleStatusUpdate(settlement.id, "completed")}
                          data-testid={`complete-${settlement.id}`}
                        >
                          <CheckCircle2 className="h-4 w-4 text-green-400" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default Settlements;
